import { handleError, post, get } from '@/lib/http-adapter';
import { User } from '@/state/user';

export type LoginProps = {
  email: string;
  password: string;
  remember_me?: boolean;
};

export type DeliveryMethod = 'email' | 'sms';

export type OtpPurpose = 'login';

export type RequestOtpProps = {
  email: string;
  delivery_method?: DeliveryMethod;
  purpose?: OtpPurpose;
};

export interface RequestOtpResponse {
  has_phone: boolean;
  delivery_method: DeliveryMethod;
  masked_phone?: string | null;
}

export type VerifyOtpProps = {
  email: string;
  otp: string;
  remember_me?: boolean;
};

export type CreateAccountProps = {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  phone: string;
  country_code: string;
};

export type VerifySignupOtpProps = {
  email: string;
  otp: string;
};

interface LoginResponse {
  user: User;
  token: string;
}

interface SignupResponse {
  message: string;
}

interface SignupOtpResponse {
  user: User;
}

type PasswordResetPayload = {
  email: string;
};

type PasswordResetResponse = {
  message: string;
};

export const login = async (loginData: LoginProps): Promise<any> => {
  try {
    return await post<LoginResponse, LoginProps>('/public/auth/login', {
      ...loginData,
    });
  } catch (error) {
    handleError(error);
  }
};

export const logout = async () => {
  try {
    const response = await post('/public/auth/logout', {});
    return response;
  } catch (error) {
    handleError(error);
  }
};

export const requestPasswordReset = async (
  passwordResetPayload: PasswordResetPayload
): Promise<PasswordResetResponse | undefined> => {
  try {
    return await post<{ message: string }, { email: string }>(
      '/public/auth/forgot-password',
      { ...passwordResetPayload }
    );
  } catch (error) {
    handleError(error);
  }
};

export const verifyResetPasswordToken = async (token: string) => {
  try {
    return await get<{
      valid: boolean;
    }>('/public/auth/verify-reset-token', { params: { token } });
  } catch (error) {
    handleError(error);
  }
};

export const resetPassword = async (passwordResetPayload: {
  token: string;
  password: string;
}) => {
  try {
    return await post('/public/auth/reset-password', {
      ...passwordResetPayload,
    });
  } catch (error) {
    handleError(error);
  }
};

export const setPassword = async (passwordResetPayload: {
  // Optional on purpose: an account that has never chosen a password proves
  // itself with the session alone. The backend answers CURRENT_PASSWORD_REQUIRED
  // when it does need one, which is how the caller learns to ask.
  old_password?: string;
  password: string;
}) => {
  try {
    return await post('/public/auth/set-password', { ...passwordResetPayload });
  } catch (error) {
    handleError(error);
  }
};

export const createAccount = async (
  data: CreateAccountProps
): Promise<SignupResponse | undefined> => {
  try {
    return await post<SignupResponse, CreateAccountProps>(
      '/public/auth/signup',
      data
    );
  } catch (error) {
    handleError(error);
  }
};

export const verifySignupOtp = async (
  data: VerifySignupOtpProps
): Promise<SignupOtpResponse | undefined> => {
  try {
    return await post<SignupOtpResponse, VerifySignupOtpProps>(
      '/public/auth/signup/verify-otp',
      data
    );
  } catch (error) {
    handleError(error);
  }
};

export const requestOtp = async (
  data: RequestOtpProps
): Promise<RequestOtpResponse | undefined> => {
  try {
    return await post<RequestOtpResponse, RequestOtpProps>(
      '/public/auth/request-otp',
      data
    );
  } catch (error) {
    handleError(error);
  }
};

export const verifyOtp = async (
  data: VerifyOtpProps
): Promise<LoginResponse | undefined> => {
  try {
    return await post<LoginResponse, VerifyOtpProps>(
      '/public/auth/verify-otp',
      data
    );
  } catch (error) {
    handleError(error);
  }
};

export const requestEmailCode = async (data: {
  email: string;
}): Promise<{ message: string } | undefined> => {
  try {
    return await post<{ message: string }>('/public/auth/email-code', data);
  } catch (error) {
    handleError(error);
  }
};

export const confirmEmailCode = async (data: {
  email: string;
  otp: string;
}): Promise<SignupOtpResponse | undefined> => {
  try {
    return await post<SignupOtpResponse>('/public/auth/email-confirm', data);
  } catch (error) {
    handleError(error);
  }
};
